import React from 'react';
import { Box, Stack, Button, Avatar, Tooltip, Typography } from 'omaha-design-system';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Plus as AddIcon, Minus as RemoveIcon, Lock as LockIcon, PencilSimple as EditIcon } from '@phosphor-icons/react';
import { BetSlipItem, PlayerPropSportsbook } from '../data/types';
import { getInjuryBadge } from '../data/transformPropsData';
import { getStatDecimals } from '../data/statLabels';
import { leagueHasMinutes } from '../data/leagueConfig';
import { MobileBetCard } from './MobileBetCard';
import { SportsbookLogo } from './SportsbookLogo';
import BattingOrderBadge from './BattingOrderBadge';

export interface InjuredTeammate {
  name: string;
  status?: string;
  notes?: string;
}

export interface PropBet {
  stat: string;
  originalStat: string;
  projection: number;
  sportsbooks: PlayerPropSportsbook[];
  // Best book fields (flattened from sportsbooks[0])
  book: string;
  bookIcon: string;
  bookLine: number;
  odds: string;
  oddsNum: number;
  delta: number;
  edge: number;
  bookWin: number;
  modelWin: number;
  marketWin: number;
  stokWin: number;
  evPct: number;
  side: 'Over' | 'Under';
  deepLinkUrl?: string;
  // Player info
  playerName: string;
  playerId: string;
  team: string;
  avatar: string;
  position: string;
  minutes: string;
  /** Custom minutes if player has customizations */
  customMinutes?: number;
  /** Whether this player has custom projections applied */
  hasCustomizations?: boolean;
  matchup: string;
  startDate?: string;
  projections: Record<string, string>;
  injuryStatus?: string;
  injuryNotes?: string;
  injuredTeammates?: InjuredTeammate[];
  /** Batting order position (1-9 for hitters, 0 for pitcher). */
  battingOrder?: number;
  /** Whether the player's lineup is confirmed (true), probable (false), or not playing (null). */
  confirmedLineup?: boolean | null;
  /** Handedness: "L", "R", or "S". */
  hand?: string;
  // Row index within the table (set by TopBetsTable)
  _rowIndex?: number;
  /**
   * Non-subscriber teaser row. The stokastic-site proxy stripped its projection, line,
   * odds, EV and books before the response left the server; the value fields above hold
   * NaN / '' and must not be rendered. Cells check this first.
   */
  locked?: boolean;
  /** EV-desc ordinal among locked rows (order without value). */
  lockedRank?: number;
}

// Helper component for locked cells
const LockedCell: React.FC = () => (
  <LockIcon size={16} color="rgba(255, 255, 255, 0.3)" />
);

/** A row is locked when the server said so — never inferred client-side. */
export const isRowLocked = (row: Pick<PropBet, 'locked'>): boolean => !!row.locked;

export const getPropsColumns = (
  handleAddToBetSlip: (bet: BetSlipItem) => void,
  betSlip: BetSlipItem[],
  handleRemoveFromBetSlip: (index: number) => void,
  /** Teaser mode (non-subscriber): header sorting is disabled so the free row stays on top. */
  isTeaser: boolean,
  league: string
): GridColDef[] => {
  const cols: GridColDef[] = [
  {
    field: 'player',
    headerName: 'Player',
    width: 280,
    minWidth: 260,
    headerAlign: 'left',
    valueGetter: (params: { row: PropBet }) => params.row.playerName,
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      const { playerName, avatar, position, matchup, team, injuryStatus, injuryNotes, injuredTeammates, battingOrder, confirmedLineup, hand } = params.row;
      const initials = playerName
        .split(' ')
        .map((n: string) => n[0])
        .join('');
      const injuryBadge = getInjuryBadge(injuryStatus);

      // Bold the player's team in the matchup string
      const renderMatchup = () => {
        if (!matchup || !team) return matchup;
        const teamIndex = matchup.indexOf(team);
        if (teamIndex === -1) return matchup;
        const before = matchup.substring(0, teamIndex);
        const after = matchup.substring(teamIndex + team.length);
        return (
          <>
            {before}<Box component="span" fontWeight={700} sx={{ color: '#fff' }}>{team}</Box>{after}
          </>
        );
      };

      // Build tooltip for injured teammates
      const injuredTeammatesWithStatus = injuredTeammates?.filter(t => t.status) || [];
      const teammateTooltip = injuredTeammatesWithStatus.length > 0
        ? injuredTeammatesWithStatus.map(t => `${t.name} (${t.status})`).join(', ')
        : '';

      return (
        <Stack direction="row" alignItems="center" spacing={1.5} py={1}>
          <Avatar src={avatar} sx={{ width: 32, height: 32, fontSize: 12 }}>
            {initials}
          </Avatar>
          <Stack>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography variant="body2" fontWeight={600} color="#42a5f5" noWrap>
                {playerName}
              </Typography>
              {injuryBadge && (
                <Tooltip title={injuryNotes || injuryStatus || ''} arrow>
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.125,
                      borderRadius: '4px',
                      backgroundColor: injuryBadge.bgColor,
                      fontSize: '10px',
                      fontWeight: 700,
                      color: injuryBadge.textColor,
                      lineHeight: 1.4,
                      flexShrink: 0,
                    }}
                  >
                    {injuryBadge.label}
                  </Box>
                </Tooltip>
              )}
              {injuredTeammatesWithStatus.length > 0 && (
                <Tooltip title={`Teammate${injuredTeammatesWithStatus.length > 1 ? 's' : ''} injured: ${teammateTooltip}`} arrow>
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.125,
                      borderRadius: '4px',
                      backgroundColor: 'rgba(245, 124, 0, 0.2)',
                      border: '1px solid rgba(245, 124, 0, 0.6)',
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#f57c00',
                      lineHeight: 1.4,
                      flexShrink: 0,
                    }}
                  >
                    TM ({injuredTeammatesWithStatus.length})
                  </Box>
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {battingOrder !== undefined && (
                <BattingOrderBadge battingOrder={battingOrder} confirmedLineup={confirmedLineup} />
              )}
              <Typography variant="caption" color="text.secondary">
                {position ? `${position}${hand ? ` (${hand})` : ''} - ` : ''}{renderMatchup()}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      );
    },
  },
  {
    field: 'minutes',
    headerName: 'Min',
    width: 75,
    headerAlign: 'center',
    align: 'center',
    sortable: true,
    valueGetter: (params) => params.row.customMinutes ?? (parseFloat(params.row.minutes) || 0),
    renderHeader: () => (
      <Tooltip title="Projected minutes" arrow>
        <span>Min</span>
      </Tooltip>
    ),
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      const { minutes, customMinutes, hasCustomizations } = params.row;
      const displayMinutes = customMinutes != null ? customMinutes.toFixed(1) : minutes;

      if (hasCustomizations) {
        return (
          <Tooltip title="Minutes adjusted by user" arrow>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <EditIcon size={14} color="#FFA726" />
              <Typography variant="body2" color="#FFA726" fontWeight={500}>
                {displayMinutes || '-'}
              </Typography>
            </Stack>
          </Tooltip>
        );
      }

      return (
        <Typography variant="body2" color="text.secondary">
          {displayMinutes || '-'}
        </Typography>
      );
    },
  },
  {
    field: 'stat',
    headerName: 'Stat',
    // NFL labels are longer ("Pass+Rush Yds") than the 2-4 char codes other leagues use
    width: league.toUpperCase() === 'NFL' ? 118 : 70,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params: GridRenderCellParams<PropBet>) => (
      <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
    ),
  },
  {
    field: 'projection',
    headerName: 'Origination Proj.',
    width: 110,
    headerAlign: 'center',
    align: 'center',
    sortable: true,
    type: 'number',
    valueGetter: (params) => (isRowLocked(params.row) ? -Infinity : params.row.projection),
    renderHeader: () => (
      <Tooltip title="Stokastic Origination Projections: Bottom-up projections driven by proprietary player-level data and simulation-based modeling." arrow>
        <Box sx={{ whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.2 }}>
          <span style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>Origination<br />Proj.</span>
        </Box>
      </Tooltip>
    ),
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      if (isRowLocked(params.row)) {
        return <LockedCell />;
      }
      return <Typography variant="body2" fontWeight={500}>{params.row.projection.toFixed(getStatDecimals(params.row.stat, league))}</Typography>;
    },
  },
  {
    field: 'bestLine',
    headerName: 'Best Line',
    width: 130,
    headerAlign: 'center',
    align: 'center',
    sortable: true,
    type: 'number',
    valueGetter: (params) => (isRowLocked(params.row) ? -Infinity : params.row.bookLine),
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      if (isRowLocked(params.row)) {
        return <LockedCell />;
      }
      const { bookLine, odds, side, bookIcon, book } = params.row;
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">
            {side === 'Under' ? 'u' : 'o'}
            {bookLine.toFixed(1)} {odds}
          </Typography>
          <SportsbookLogo book={book} bookIcon={bookIcon} size={20} borderRadius="4px" />
        </Stack>
      );
    },
  },
  {
    field: 'trueOdds',
    headerName: 'Market Proj.',
    width: 110,
    headerAlign: 'center',
    align: 'center',
    sortable: true,
    type: 'number',
    valueGetter: (params) => {
      // Sort by marketWin probability (higher probability = more likely to hit)
      const marketWin = params.row.marketWin;
      if (!marketWin || marketWin === 0) return -Infinity;
      return marketWin;
    },
    renderHeader: () => (
      <Tooltip title="Stokastic Market Projections: Top-down true-odds modeling derived from optimized weighting of sharp sportsbook prices." arrow>
        <Box sx={{ whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.2 }}>
          <span style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>Market<br />Proj.</span>
        </Box>
      </Tooltip>
    ),
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      if (isRowLocked(params.row)) {
        return <LockedCell />;
      }
      // Calculate true odds from market true win probability (vig-free)
      const marketWin = params.row.marketWin;
      if (marketWin === null || marketWin === undefined || marketWin === 0) {
        return <Typography variant="body2" color="text.secondary">-</Typography>;
      }
      // Convert probability to American odds
      let trueOdds: number;
      if (marketWin >= 0.5) {
        trueOdds = Math.round(-100 * marketWin / (1 - marketWin));
      } else {
        trueOdds = Math.round(100 * (1 - marketWin) / marketWin);
      }
      return (
        <Typography variant="body2" color="text.secondary">
          {trueOdds > 0 ? '+' : ''}{trueOdds}
        </Typography>
      );
    },
  },
  {
    field: 'modelWin',
    headerName: 'Model Win %',
    width: 110,
    headerAlign: 'center',
    align: 'center',
    sortable: true,
    type: 'number',
    valueGetter: (params) => (isRowLocked(params.row) ? -Infinity : params.row.modelWin ?? -Infinity),
    renderHeader: () => (
      <Tooltip title="Model probability derived from your blend of projection and market true odds vs the line">
        <span>Model Win %</span>
      </Tooltip>
    ),
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      if (isRowLocked(params.row)) {
        return <LockedCell />;
      }
      const modelWin = params.row.modelWin;
      return (
        <Typography variant="body2" fontWeight={500} color="#66BB6A">
          {modelWin !== null ? `${(modelWin * 100).toFixed(0)}%` : '-'}
        </Typography>
      );
    },
  },
  {
    field: 'evPct',
    headerName: 'EV %',
    width: 80,
    headerAlign: 'center',
    align: 'center',
    sortable: true,
    type: 'number',
    valueGetter: (params) => (isRowLocked(params.row) ? -Infinity : params.row.evPct ?? -Infinity),
    renderHeader: () => (
      <Tooltip title="The % you can expect your bankroll to increase per bet">
        <span>EV %</span>
      </Tooltip>
    ),
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      if (isRowLocked(params.row)) {
        return <LockedCell />;
      }
      const evPct = params.row.evPct;
      return (
        <Typography
          variant="body2"
          fontWeight={600}
          color={evPct !== null && evPct >= 0 ? '#66BB6A' : '#EF5350'}
        >
          {evPct !== null
            ? `${evPct >= 0 ? '+' : ''}${evPct.toFixed(1)}%`
            : '-'}
        </Typography>
      );
    },
  },
  {
    field: 'otherBooks',
    headerName: 'Other Books',
    flex: 1,
    minWidth: 200,
    headerAlign: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      if (isRowLocked(params.row)) {
        return <LockedCell />;
      }
      const { sportsbooks } = params.row;
      // Skip the first one (best book) and show the rest
      const otherBooks = sportsbooks.slice(1);

      if (otherBooks.length === 0) {
        return (
          <Typography variant="caption" color="text.secondary">
            -
          </Typography>
        );
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
            overflowX: 'auto',
            overflowY: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
            scrollbarWidth: 'none',
            cursor: 'grab',
            '&::-webkit-scrollbar': { display: 'none' },
            '&:active': { cursor: 'grabbing' },
          }}
          onMouseDown={(e) => {
            const el = e.currentTarget;
            el.dataset.isDragging = 'true';
            el.dataset.startX = String(e.pageX - el.offsetLeft);
            el.dataset.scrollLeft = String(el.scrollLeft);
          }}
          onMouseMove={(e) => {
            const el = e.currentTarget;
            if (el.dataset.isDragging !== 'true') return;
            e.preventDefault();
            const x = e.pageX - el.offsetLeft;
            const walk = (x - Number(el.dataset.startX)) * 1.5;
            el.scrollLeft = Number(el.dataset.scrollLeft) - walk;
          }}
          onMouseUp={(e) => {
            e.currentTarget.dataset.isDragging = 'false';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.dataset.isDragging = 'false';
          }}
        >
          {otherBooks.map((sb) => {
            const { playerName, playerId, avatar, stat, projection, edge } = params.row;
            return (
              <Stack
                key={sb.book}
                direction="row"
                alignItems="center"
                spacing={0.5}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToBetSlip({
                    id: `${playerId}-${stat}-${sb.book}-${sb.side}`,
                    playerName,
                    avatar,
                    stat,
                    projection: String(projection.toFixed(1)),
                    bookLine: String(sb.line.toFixed(1)),
                    edge: String(edge.toFixed(1)),
                    bookWin: sb.bookWin,
                    modelWin: sb.modelWin,
                    evPct: sb.evPct,
                    book: sb.book,
                    bookLogo: sb.bookIcon,
                    odds: sb.americanOdds,
                    side: sb.side,
                    deepLinkUrl: sb.deepLinkUrl,
                  });
                }}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '4px',
                  px: 1,
                  py: 0.25,
                  flexShrink: 0,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <SportsbookLogo book={sb.book} bookIcon={sb.bookIcon} size={18} borderRadius="3px" />
                <Typography variant="caption" color="text.primary">
                  {sb.side === 'Under' ? 'u' : 'o'}{sb.line.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({sb.americanOdds})
                </Typography>
              </Stack>
            );
          })}
        </Box>
      );
    },
  },
  {
    field: 'action',
    headerName: '',
    width: 150,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      if (isRowLocked(params.row)) {
        return <LockedCell />;
      }
      const bet = params.row;
      // Include book and side in betId to match matchups page format
      const betId = `${bet.playerId}-${bet.stat}-${bet.book}-${bet.side}`;
      const betSlipIndex = betSlip.findIndex((item) => item.id === betId);
      const isInBetSlip = betSlipIndex !== -1;

      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="text"
            size="small"
            startIcon={isInBetSlip ? <RemoveIcon size={14} /> : <AddIcon size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              if (isInBetSlip) {
                handleRemoveFromBetSlip(betSlipIndex);
              } else {
                handleAddToBetSlip({
                  id: betId,
                  playerName: bet.playerName,
                  avatar: bet.avatar,
                  stat: bet.stat,
                  projection: String(bet.projection.toFixed(1)),
                  bookLine: String(bet.bookLine.toFixed(1)),
                  edge: String(bet.edge.toFixed(1)),
                  bookWin: bet.bookWin,
                  modelWin: bet.modelWin,
                  evPct: bet.evPct,
                  book: bet.book,
                  bookLogo: bet.bookIcon,
                  odds: bet.odds,
                  side: bet.side,
                  deepLinkUrl: bet.deepLinkUrl,
                });
              }
            }}
            sx={{
              color: isInBetSlip ? '#EF5350' : '#42A5F5',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'none',
              px: 1,
              py: 0.5,
              width: '88px',
              '&:hover': {
                backgroundColor: isInBetSlip ? 'rgba(239, 83, 80, 0.08)' : 'rgba(66, 165, 245, 0.08)',
              },
            }}
          >
            {isInBetSlip ? 'Remove' : 'Add'}
          </Button>
          <Button
            variant="outlined"
            color="success"
            size="small"
            disabled={!bet.deepLinkUrl}
            onClick={(e) => {
              e.stopPropagation();
              if (bet.deepLinkUrl) {
                window.open(bet.deepLinkUrl, '_blank');
              }
            }}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'none',
              px: 1.5,
              py: 0.5,
              width: '50px',
            }}
          >
            Bet
          </Button>
        </Stack>
      );
    },
  },
  ];
  const leagueCols = leagueHasMinutes(league) ? cols : cols.filter(col => col.field !== 'minutes');
  // Locked rows have nothing to sort by; freezing the headers keeps the free row on top.
  return isTeaser ? leagueCols.map(col => ({ ...col, sortable: false })) : leagueCols;
};

// Mobile column that renders cards as single rows
export const getMobilePropsColumns = (
  handleAddToBetSlip: (bet: BetSlipItem) => void,
  betSlip: BetSlipItem[],
  handleRemoveFromBetSlip: (index: number) => void,
  onOtherBooksClick?: (bet: PropBet) => void,
  expandedMobileRowId?: string | null,
  onToggleMobileExpand?: (id: string) => void,
): GridColDef[] => [
  {
    field: 'card',
    headerName: '',
    flex: 1,
    minWidth: 0,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PropBet>) => {
      const bet = params.row;
      // Include book and side in betId to match matchups page format
      const betId = `${bet.playerId}-${bet.stat}-${bet.book}-${bet.side}`;
      const betSlipIndex = betSlip.findIndex((item) => item.id === betId);
      const isInBetSlip = betSlipIndex !== -1;
      const isLocked = isRowLocked(bet);
      const rowId = String(params.id);
      return (
        <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
          <MobileBetCard
            bet={bet}
            isInBetSlip={isInBetSlip}
            isLocked={isLocked}
            isExpanded={expandedMobileRowId === rowId}
            onAddToBetSlip={handleAddToBetSlip}
            onRemoveFromBetSlip={isInBetSlip ? () => handleRemoveFromBetSlip(betSlipIndex) : undefined}
            onOtherBooksClick={onOtherBooksClick && !isLocked ? () => onOtherBooksClick(bet) : undefined}
            // Locked cards have no odds panel to expand into — no caret, no toggle
            onToggleExpand={onToggleMobileExpand && !isLocked ? () => onToggleMobileExpand(rowId) : undefined}
          />
        </Box>
      );
    },
  },
];
